import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, message, Row, Table } from "antd";
import {
  FaClipboardList,
  FaFilePdf,
  FaPills,
  FaStethoscope,
  FaVial,
  FaXRay,
} from "react-icons/fa";
import {
  hasConclusionContent,
  HealthConclusion,
  SpecialtyExams,
} from "../../components/KetQuaKsk";
import styles from "./dangKyKcb.module.css";
import { useLocation } from "react-router-dom";
import "react-image-gallery/styles/css/image-gallery.css";
import Login from "../../components/Login/login";
import KetQuaCard from "../../components/KetQuaCard";
import LabResults from "../../components/LabResults";
import { kcbService } from "../../services/kcbService";
import Loading from "../../components/common/Loading/Loading";
import PatientInfo from "../../components/common/PatientInfo/PatientInfo";
import CustomSelect from "../../components/common/CustomSelect/CustomSelect";
import Label from "../../components/common/Label/Label";

const DangKyKcb = () => {
  const [loading, setLoading] = useState(true);
  const [luotKham, setLuotKham] = useState([]);
  const [data, setData] = useState({
    hoTen: "",
    ngaySinh: "",
    benhVien: "",
    chanDoan: "",
  });
  const [cdha, setCdha] = useState([]);
  const [xn, setXn] = useState([]);
  const [thuoc, setThuoc] = useState([]);
  const [isLogin, setIsLogin] = useState(false);
  // WareHouseDetailDto của lượt khám đang chọn — dùng chung với app mobile.
  const [ketQua, setKetQua] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { search } = useLocation();
  const makcb = new URLSearchParams(search).get("makcb");
  const mandk = new URLSearchParams(search).get("mandk");
  localStorage.setItem("makcb", makcb);
  const [selectedMaKcb, setSelectedMaKcb] = useState(null);
  // Mục lục: node DOM của từng mục để cuộn tới, và mục đang được xem.
  const sectionRefs = useRef({});
  const [activeTab, setActiveTab] = useState(null);

  const handleChange = useCallback(
    (value) => {
      setSelectedMaKcb(value);

      const selectedLuotKham = kcbService.getLuotKhamById(luotKham, value);
      if (selectedLuotKham) {
        setCdha(kcbService.filterCdha(selectedLuotKham.canLamSangDto));
        setXn(kcbService.filterXetNghiem(selectedLuotKham.canLamSangDto));
        setThuoc(selectedLuotKham.thuocDto);
        setData(selectedLuotKham);
      }

      // Kết quả chi tiết (chỉ số xét nghiệm, kết luận, tư vấn) nằm ở API riêng,
      // dùng chung DTO với app. Tải nền, hỏng thì phần cận lâm sàng vẫn hiện.
      kcbService.getKetQuaKham(value, mandk).then(setKetQua);
    },
    [luotKham, mandk],
  );

  // Set mặc định khi luotKham có dữ liệu (ví dụ chọn phần tử đầu tiên)
  useEffect(() => {
    if (luotKham?.length > 0) {
      handleChange(makcb);
    }
  }, [handleChange, luotKham, makcb]);

  useEffect(() => {
    const fetchData = async () => {
      if (!makcb || !mandk || !isLogin) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await kcbService.getLichSuKCB(makcb, mandk);
        setData(result.data[0]);
        setLuotKham(result.data);
      } catch (error) {
        console.error("Lỗi tải dữ liệu:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [makcb, mandk, isLogin]);

  // ── Chip sáng theo mục đang xem khi người dùng tự cuộn ──────────────
  //      rootMargin cắt phần trên đúng bằng chiều cao thanh dính và bỏ 55%
  //      phía dưới, để chỉ mục đang nằm trong vùng đọc mới được tính.
  // Dữ liệu khám sức khoẻ định kỳ đã có sẵn trong response, trước đây trang
  // không dùng tới. Mobile lọc !laKetLuan cho bảng chuyên khoa, còn dòng
  // laKetLuan chính là kết luận chung — làm giống hệt để hai bên khớp nhau.
  const ksk = data?.khamSucKhoeDinhKy;
  const kqKsk = data?.ketQuaKhamSucKhoe || [];
  // Ưu tiên khamSucKhoe của DTO mới: backend đã dịch sẵn maLoai thành "Loại I…"
  // và đã lọc bỏ dòng kết luận, khỏi phải làm lại ở phía web.
  const specialtyRows =
    ketQua?.khamSucKhoe?.length > 0
      ? ketQua.khamSucKhoe
      : kqKsk
          .filter((r) => !r.laKetLuan)
          .sort((a, b) => (a.sapXep || 0) - (b.sapXep || 0));
  const ketLuanChung =
    ketQua?.ketLuanCuoi || kqKsk.find((r) => r.laKetLuan)?.ketLuan;
  const showConclusion = hasConclusionContent(ksk, ketLuanChung);

  // Chỉ số xét nghiệm chỉ có ở DTO mới; không có thì giữ danh sách dịch vụ cũ.
  const chiSoXetNghiem = ketQua?.xetNghiem || [];

  const sectionKeys = [
    showConclusion ? "ketluan" : null,
    specialtyRows.length > 0 ? "ksk" : null,
    chiSoXetNghiem.length > 0 || xn?.length > 0 ? "xn" : null,
    cdha?.length > 0 ? "cdha" : null,
    thuoc?.length > 0 ? "thuoc" : null,
  ]
    .filter(Boolean)
    .join(",");

  useEffect(() => {
    const els = sectionKeys
      .split(",")
      .filter(Boolean)
      .map((key) => sectionRefs.current[key])
      .filter(Boolean);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          )[0];
        if (top) setActiveTab(top.target.dataset.sectionKey);
      },
      { rootMargin: "-72px 0px -55% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionKeys]);

  // ── Mục lục: chỉ hiện tab cho mục thực sự có dữ liệu ────────────────
  //      Đồng bộ với màn CateMedicalExaminationDetail bên mobile: mỗi mục là
  //      một chip icon + tên, chạm vào thì cuộn mượt tới đúng mục.
  const tabs = [
    {
      key: "ketluan",
      label: "Kết luận",
      Icon: FaClipboardList,
      visible: showConclusion,
    },
    {
      key: "ksk",
      label: "Khám sức khỏe",
      Icon: FaStethoscope,
      visible: specialtyRows.length > 0,
    },
    {
      key: "xn",
      label: "Xét nghiệm",
      Icon: FaVial,
      visible: chiSoXetNghiem.length > 0 || xn?.length > 0,
    },
    {
      key: "cdha",
      label: "Chẩn đoán hình ảnh",
      Icon: FaXRay,
      visible: cdha?.length > 0,
    },
    {
      key: "thuoc",
      label: "Đơn thuốc",
      Icon: FaPills,
      visible: thuoc?.length > 0,
    },
  ].filter((t) => t.visible);

  // activeTab có thể trỏ tới mục đã biến mất khi đổi lượt khám -> quy về mục
  // đầu tiên đang có, tính lúc render thay vì gọi setState trong render.
  const activeKey = tabs.some((t) => t.key === activeTab)
    ? activeTab
    : tabs[0]?.key;

  const scrollToSection = (key) => {
    const el = sectionRefs.current[key];
    if (!el) return;
    setActiveTab(key);
    // scroll-margin-top trong CSS lo phần bù cho thanh mục lục dính trên đầu.
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openKetQuaPdf = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const { blob } = await kcbService.getKetQuaPdfBlob(selectedMaKcb, mandk);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Thu hồi muộn: revoke ngay thì tab vừa mở chưa kịp đọc xong blob.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      message.warning(err.message || "Không tải được phiếu trả kết quả.");
    } finally {
      setPdfLoading(false);
    }
  };

  const sectionHeader = (Icon, title) => (
    <div className={styles.sectionHeader}>
      <span className={styles.sectionIcon}>
        <Icon />
      </span>
      <span className={styles.sectionTitle}>{title}</span>
    </div>
  );

  if (loading) {
    return <Loading tip="Đang tải dữ liệu..." fullScreen={true} />;
  }

  const columnsThuoc = [
    {
      title: (
        <span style={{ color: "#219737", fontWeight: "bold" }}>Tên thuốc</span>
      ),
      key: "tenThuoc",
      dataIndex: "tenThuoc",
    },
    {
      title: "ĐVT",
      key: "tenDonViTinh",
      dataIndex: "tenDonViTinh",
      width: 50,
    },
    {
      title: "S.L",
      key: "soLuong",
      dataIndex: "soLuong",
      width: 50,
    },
    {
      title: "Số ngày",
      key: "soNgayDung",
      dataIndex: "soNgayDung",
      width: 70,
      align: "center",
    },
    {
      // Cách dùng là thông tin quan trọng nhất của đơn thuốc với người bệnh,
      // API vẫn trả về (ThuocDto.CachDung) nhưng bảng cũ không hiển thị.
      title: "Cách dùng",
      key: "cachDung",
      dataIndex: "cachDung",
    },
  ];

  return (
    <Row justify="center" style={{ padding: "16px 0px 0" }}>
      <Col xs={24} sm={22} md={20} lg={16} xl={14}>
        {/* Login */}
        {!isLogin && <Login setIsLogin={setIsLogin}></Login>}

        {/* Thông tin bệnh nhân */}
        {isLogin && (
          <>
            <div className={styles.wrapSelectSection}>
              <PatientInfo data={data} />
              <div className={styles.selectSection}>
                <Label>Lượt khám bệnh</Label>
                <CustomSelect
                  value={selectedMaKcb}
                  onChange={handleChange}
                  className={styles.visitSelect}
                  options={luotKham?.map((m, i) => ({
                    value: m.maKcb,
                    label: `Lần ${luotKham.length - i}: [${m.ngayDk}] - ${m.maKcb}`,
                  }))}
                  placeholder="Chọn lượt khám cần xem"
                />
              </div>
              {/* Phiếu trả kết quả: chỉ hiện khi hồ sơ thực sự có file PDF */}
              {ketQua?.isKetQuaPdf && (
                <button
                  type="button"
                  className={styles.pdfButton}
                  disabled={pdfLoading}
                  onClick={openKetQuaPdf}
                >
                  <FaFilePdf />
                  {pdfLoading ? "Đang tải phiếu..." : "Phiếu trả kết quả"}
                </button>
              )}

              {/* Mục lục dính trên đầu, chạm vào cuộn tới đúng mục */}
              {tabs.length > 0 && (
                <nav className={styles.tabBar}>
                  <div className={styles.tabBarInner}>
                    {tabs.map(({ key, label, Icon }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => scrollToSection(key)}
                        className={`${styles.tab} ${
                          key === activeKey ? styles.tabActive : ""
                        }`}
                      >
                        <Icon className={styles.tabIcon} />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </nav>
              )}

              {showConclusion && (
                <div
                  className={styles.section}
                  data-section-key="ketluan"
                  ref={(el) => (sectionRefs.current.ketluan = el)}
                >
                  {sectionHeader(FaClipboardList, "Kết luận")}
                  <HealthConclusion
                    ksk={ksk}
                    ketLuanChung={ketLuanChung}
                    phanLoaiCuoi={ketQua?.phanLoaiCuoi}
                  />
                </div>
              )}

              {specialtyRows.length > 0 && (
                <div
                  className={styles.section}
                  data-section-key="ksk"
                  ref={(el) => (sectionRefs.current.ksk = el)}
                >
                  {sectionHeader(FaStethoscope, "Khám sức khỏe")}
                  <SpecialtyExams rows={specialtyRows} />
                </div>
              )}

              {(chiSoXetNghiem.length > 0 || (xn && xn.length > 0)) && (
                <div
                  className={styles.section}
                  data-section-key="xn"
                  ref={(el) => (sectionRefs.current.xn = el)}
                >
                  {sectionHeader(FaVial, "Xét nghiệm")}
                  {/* Có bảng chỉ số theo từng chỉ tiêu thì ưu tiên bảng, giống
                      mobile; chưa có thì hiện danh sách dịch vụ kèm kết luận. */}
                  {chiSoXetNghiem.length > 0 ? (
                    <LabResults results={chiSoXetNghiem} />
                  ) : (
                    <KetQuaCard
                      items={xn}
                      Icon={FaVial}
                      emptyText="Không có thông tin xét nghiệm."
                    />
                  )}
                </div>
              )}

              {/* Thông tin thuốc vật tư - dịch vụ*/}
              {cdha && cdha.length > 0 && (
                <>
                  <div
                    className={styles.section}
                    data-section-key="cdha"
                    ref={(el) => (sectionRefs.current.cdha = el)}
                  >
                    {sectionHeader(FaXRay, "Chẩn đoán hình ảnh")}
                    <KetQuaCard
                      items={cdha}
                      Icon={FaXRay}
                      emptyText="Chưa có dữ liệu chẩn đoán hình ảnh."
                    />
                  </div>
                </>
              )}

              {thuoc && thuoc.length > 0 && (
                <div
                  className={styles.section}
                  data-section-key="thuoc"
                  ref={(el) => (sectionRefs.current.thuoc = el)}
                >
                  {sectionHeader(FaPills, "Đơn thuốc")}
                  <Table
                    className={"medicineTable"}
                    columns={columnsThuoc}
                    dataSource={thuoc}
                    pagination={false}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </Col>
    </Row>
  );
};

export default DangKyKcb;
